import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { InvoiceFormat } from "./InvoiceDocument";

/**
 * Renders the invoice into a print-only layer and drives `window.print()`.
 *
 * ── WHY THE BROWSER, AND NOT A PDF LIBRARY ───────────────────────────────────
 *
 * The document is Arabic. Producing a correct Arabic PDF means bidirectional
 * reordering, contextual glyph shaping (every letter has up to four forms) and
 * ligature substitution — that is a text-shaping engine, not a formatting
 * detail. `pdf-lib`, `jsPDF` and friends draw glyphs in the order given: they
 * print Arabic as disconnected, backwards letterforms. On an invoice that is
 * not a cosmetic flaw, it is a document that says the wrong thing.
 *
 * The browser already contains HarfBuzz. Printing through it gets correct
 * shaping, correct RTL, real font fallback, and the user's own "Save as PDF"
 * — which is a true vector PDF at the device's DPI.
 *
 * The trade-off is stated plainly rather than hidden: this produces a PDF
 * through the print dialog, NOT a server-generated file that could be emailed
 * or archived automatically. The upgrade path for that is Cloudflare Browser
 * Rendering, which runs the same HTML through headless Chrome — the templates
 * here are already the ones it would render, so nothing about this component
 * needs to change when that lands. See docs/INVOICE_RENDERING.md.
 *
 * ── THE @page RULE ───────────────────────────────────────────────────────────
 *
 * Page size cannot live in the stylesheet: A4, 80mm and 58mm are mutually
 * exclusive and only the active format's rule may exist. So a single <style>
 * element is written immediately before printing and removed after.
 */

const PAGE_RULES: Record<InvoiceFormat, string> = {
  // A4 with a modest margin; the document supplies its own inner padding.
  a4: "@page { size: A4 portrait; margin: 8mm; }",
  // Thermal rolls are continuous. The height is deliberately `auto` — pinning
  // it would either clip a long receipt or feed blank paper after a short one.
  thermal80: "@page { size: 80mm auto; margin: 0; }",
  thermal58: "@page { size: 58mm auto; margin: 0; }",
};

export function PrintPortal({
  format,
  onDone,
  children,
}: {
  format: InvoiceFormat;
  /** Fires after the print dialog closes, so the caller can unmount this. */
  onDone: () => void;
  children: React.ReactNode;
}) {
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-lp-print", "1");
    style.textContent = PAGE_RULES[format];
    document.head.append(style);

    // `afterprint` is how the browser says the dialog closed — whether the
    // user printed or cancelled. Polling or a timeout would guess.
    const finish = () => doneRef.current();
    window.addEventListener("afterprint", finish);

    // Wait a frame so the portal's contents are laid out (and the QR's SVG
    // path is built) before the dialog snapshots the page.
    //
    // rAF alone is not enough. A backgrounded tab gets no animation frames at
    // all, so if someone hits Print and immediately switches away, the dialog
    // would never open AND `afterprint` would never fire — leaving the portal
    // mounted with no way out. The timeout is the floor: whichever arrives
    // first fires the print exactly once.
    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      window.print();
    };
    const raf = requestAnimationFrame(() => requestAnimationFrame(fire));
    const fallback = window.setTimeout(fire, 120);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(fallback);
      window.removeEventListener("afterprint", finish);
      style.remove();
    };
  }, [format]);

  return createPortal(
    <div className="lp-print-root">{children}</div>,
    document.body,
  );
}
